import type {MonthColumn} from "../../utils";
import {rowLabel} from "../../utils";
import {ReportRowKind, type PeriodicViewRow} from "../../types";
import {SignedMoneyCell} from "../StatementTable/AmountCells.tsx";
import {AccountLabelCell} from "./AccountLabelCell.tsx";
import {
    COL_SEP_L,
    hasLinesInPeriod,
    isDrillableAccount,
    rowClassName,
} from "./tableStyles.ts";
import type {LegalEntityType} from "../../../../../generated/enums.ts";

interface PeriodicTableRowProps {
    row: PeriodicViewRow;
    monthColumns: MonthColumn[];
    ytdMonthIndex: number;
    currency: string;
    legalEntityType: LegalEntityType;
    onToggleNode: (id: string) => void;
    onAccountClick: (row: PeriodicViewRow) => void;
    onCellClick: (row: PeriodicViewRow, periodIndex: number) => void;
    onKpiClick: (row: PeriodicViewRow) => void;
}

export function PeriodicTableRow({
    row,
    monthColumns,
    ytdMonthIndex,
    currency,
    legalEntityType,
    onToggleNode,
    onAccountClick,
    onCellClick,
    onKpiClick,
}: PeriodicTableRowProps) {
    const expandable = row.kind === ReportRowKind.Group;
    const clickableKpi = row.kind === ReportRowKind.Kpi;
    const drillable = isDrillableAccount(row);
    const label = rowLabel(row, legalEntityType);

    const onRowActivate = () => {
        if (expandable) onToggleNode(row.id);
        else if (clickableKpi) onKpiClick(row);
        else if (drillable) onAccountClick(row);
    };

    const cursor = expandable || clickableKpi || drillable ? "cursor-pointer" : "";
    const ytdCell = row.cells[ytdMonthIndex] ?? null;

    return (
        <tr
            className={`group ${rowClassName(row)} ${cursor}`}
            onClick={onRowActivate}
        >
            <AccountLabelCell
                row={row}
                label={label}
                currency={currency}
                expandable={expandable}
                expanded={row.expanded}
                drillable={drillable}
                showKpiBadge={clickableKpi}
            />
            {monthColumns.map((column, index) => {
                const cell = row.cells[index] ?? null;
                const cellDrillable =
                    row.kind === ReportRowKind.Account && hasLinesInPeriod(row, index);
                return (
                    <td
                        key={column.label}
                        className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${
                            index === 0 ? "" : COL_SEP_L
                        } ${cellDrillable ? "cursor-pointer hover:bg-[#EAF5FD]" : ""}`}
                        onClick={
                            cellDrillable
                                ? (event) => {
                                      event.stopPropagation();
                                      onCellClick(row, index);
                                  }
                                : undefined
                        }
                    >
                        {cell && <SignedMoneyCell money={cell.signed_net} />}
                    </td>
                );
            })}
            <td
                className={`px-3 py-2 text-right tabular-nums whitespace-nowrap bg-[#F4FAFE] ${COL_SEP_L}`}
            >
                {ytdCell && <SignedMoneyCell money={ytdCell.cumulative_signed_net} />}
            </td>
            <td
                className={`px-3 py-2 text-right tabular-nums whitespace-nowrap font-semibold ${COL_SEP_L}`}
            >
                <SignedMoneyCell money={row.total_signed_net} />
            </td>
        </tr>
    );
}
